import { Epic, ofType } from "redux-observable";
import { RootState } from "../../store";
import * as authSelectors from "../auth/authSelectors";
import * as actions from "./adminUserActions";
import { ServiceContainer } from "../../serviceContainer";
import { AjaxError } from "rxjs/ajax";
import { extractAjaxError } from "../../error-handler";
import { catchError, exhaustMap, map, mergeMap, tap } from "rxjs/operators";
import { of } from "rxjs";

export const adminUserListEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.LIST_ADMIN_USERS),
    exhaustMap((action: actions.ListAdminUsersAction) => {
      return services.apiAdmin
        .adminUsers(
          authSelectors.accessToken(state$.value),
          action.organization
        )
        .pipe(
          map((response) =>
            actions.listAdminUsersSuccessAction(action.organization, response)
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching admin user list"
            );
            return of(actions.listAdminUsersFailAction(action.organization));
          })
        );
    })
  );

export const getAdminUserEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.GET_ADMIN_USER),
    exhaustMap((action: actions.GetAdminUserAction) => {
      return services.apiAdmin
        .getAdminUser(
          authSelectors.accessToken(state$.value),
          action.organization,
          action.id
        )
        .pipe(
          map((response) =>
            actions.getAdminUserSuccessAction(
              action.organization,
              action.id,
              response.data
            )
          ),
          catchError(() => {
            services.toastr.error(
              "Fail",
              "There was error while fetching admin user"
            );
            return of(
              actions.getAdminUserFailAction(action.organization, action.id)
            );
          })
        );
    })
  );

export const upsertAdminUser: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.UPSERT_ADMIN_USER),
    exhaustMap((action: actions.UpsertAdminUserAction) => {
      const state = state$.value;
      let upsert: any;

      if (!action.id) {
        upsert = services.apiAdmin.insert(
          authSelectors.accessToken(state),
          action.organization,
          action.data
        );
      } else {
        upsert = services.apiAdmin.update(
          authSelectors.accessToken(state),
          action.organization,
          action.data,
          action.id
        );
      }

      return upsert.pipe(
        tap((x: any) =>
          services.toastr.message(
            !action.id
              ? "Admin was added successfully"
              : "Admin was updated successfully",
            `Generated password is: "${x.password}"`
          )
        ),
        mergeMap(() =>
          of(actions.upsertAdminUserSuccessAction(action.organization, ""))
        ),
        catchError((x: AjaxError) => {
          const errorMessage = extractAjaxError({ error: x });
          services.toastr.error("Error", errorMessage);
          return of(actions.upsertAdminUserFailAction(action.organization));
        })
      );
    })
  );

export const modifyRoleEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(actions.MODIFY_ROLE),
    exhaustMap((action: actions.ModifyRoleAction) => {
      const state = state$.value;

      return services.apiAdmin
        .modifyRole(
          authSelectors.accessToken(state),
          action.organization,
          action.userId,
          action.role,
          action.add
        )
        .pipe(
          map(() => actions.modifyRoleSuccessAction(action.organization)),
          tap(() =>
            services.toastr.success(
              "Success",
              action.add ? "Role added" : "Role removed"
            )
          ),
          catchError((x: AjaxError) => {
            services.toastr.error(
              "Error",
              `There was error while modifying role for user`
            );
            return of(actions.modifyRoleFailAction(action.organization));
          })
        );
    })
  );
